import React from 'react';
import styled from 'styled-components';
import { ChevronRight, Home, Store, ShoppingCart, History, FileText } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius } from '../../styles/ThemeStyles';

interface BreadcrumbItem {
  label: string;
  icon?: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin: ${Spacing.lg} 0;
  padding: ${Spacing.md};
  background: linear-gradient(135deg, #f8f9fa, #ffffff);
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const BreadcrumbItem = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border: none;
  background: transparent;
  color: ${props => props.$isActive ? ThemeColors.primary.main : ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${props => props.$isActive ? Typography.fontWeight.semiBold : Typography.fontWeight.medium};
  cursor: pointer;
  border-radius: ${BorderRadius.sm};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${ThemeColors.background.default};
    color: ${ThemeColors.primary.main};
  }
  
  ${props => props.$isActive && `
    background: ${ThemeColors.primary.main};
    color: white;
  `}
`;

const BreadcrumbSeparator = styled.span`
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.sm};
`;

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  return (
    <BreadcrumbContainer>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <BreadcrumbItem 
            $isActive={item.isActive}
            onClick={item.onClick}
            disabled={!item.onClick}
          >
            {item.icon}
            {item.label}
          </BreadcrumbItem>
          {index < items.length - 1 && (
            <BreadcrumbSeparator>
              <ChevronRight size={14} />
            </BreadcrumbSeparator>
          )}
        </React.Fragment>
      ))}
    </BreadcrumbContainer>
  );
};

// Helper function to create common breadcrumb patterns
export const createBreadcrumbItems = (
  storeName: string,
  currentPage: string,
  onNavigate: (tab: 'store-visit' | 'store-order' | 'store-history') => void,
  onDashboardClick?: () => void
): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      icon: <Home size={14} />,
      onClick: onDashboardClick || (() => onNavigate('store-visit'))
    },
    {
      label: storeName,
      icon: <Store size={14} />,
      onClick: () => onNavigate('store-visit')
    }
  ];

  // Add page-specific items
  switch (currentPage) {
    case 'store-visit':
      items.push({
        label: 'Store Visit',
        icon: <Store size={14} />,
        isActive: true
      });
      break;
    case 'store-order':
      items.push({
        label: 'Store Order',
        icon: <ShoppingCart size={14} />,
        isActive: true
      });
      break;
    case 'store-history':
      items.push({
        label: 'Order History',
        icon: <History size={14} />,
        isActive: true
      });
      break;
    case 'order-preview':
      items.push(
        {
          label: 'Store Order',
          icon: <ShoppingCart size={14} />,
          onClick: () => onNavigate('store-order')
        },
        {
          label: 'Order Preview',
          icon: <FileText size={14} />,
          isActive: true
        }
      );
      break;
    case 'historical-order-preview':
      items.push(
        {
          label: 'Order History',
          icon: <History size={14} />,
          onClick: () => onNavigate('store-history')
        },
        {
          label: 'Order Preview',
          icon: <FileText size={14} />,
          isActive: true
        }
      );
      break;
  }

  return items;
};
